import dayjs from "dayjs";
import * as AuthSession from "expo-auth-session";
import { router } from "expo-router";
import { jwtDecode } from "jwt-decode";
import { autorun } from "mobx";
import {
  cast,
  flow,
  types,
  type Instance,
  type SnapshotIn,
  type SnapshotOut,
} from "mobx-state-tree";
import { z } from "zod";
import { constants } from "~/constants";
import { environment } from "~/environment";
import { assert } from "~/utils/assert";
import { delay } from "~/utils/delay";
import { User } from "./entities/user/User";
import { getEnv } from "./utils/getEnv";
import { getRoot } from "./utils/getRoot";
import type { UserInstance } from "./entities/user/User";
import type { Await } from "./utils/Await";
import type { Href } from "expo-router";

export interface AuthStoreInstance extends Instance<typeof AuthStore> {}
export interface AuthStoreSnapshotIn extends SnapshotIn<typeof AuthStore> {}
export interface AuthStoreSnapshotOut extends SnapshotOut<typeof AuthStore> {}

export const AuthStore = types
  .model("AuthStore", {
    activeUser: types.safeReference(User),
    activeUserNameFromAuthProvider: types.optional(types.string, ""),
    accessToken: types.maybe(types.string),
    refreshToken: types.maybe(types.string),
  })
  .views((self) => {
    return {
      get isLoggedIn() {
        return !!self.accessToken && !!self.activeUser;
      },
    };
  })
  .actions((self) => {
    return {
      setActiveUser(idOrInstance: string | UserInstance) {
        self.activeUser = cast(idOrInstance);
        return self.activeUser;
      },
      clearActiveUser() {
        self.activeUser = undefined;
      },
    };
  })
  .actions((self) => {
    return {
      getActiveUser: flow<UserInstance, []>(function* getActiveUser(): any {
        const env = getEnv(self);
        const root = getRoot(self);

        const response = yield env.baseHttp.get("clients/me");

        const user = root.userStore.processOne(response.data.data);

        self.setActiveUser(user);

        return user;
      }),

      createNewUser: flow<
        UserInstance,
        [
          {
            firstName: string;
            lastName: string;
            streetAddress: string;
            zipCode: string;
            city: string;
            email: string;
            phoneNumber: string;
          },
        ]
      >(function* createNewUser(userDataArg): any {
        const env = getEnv(self);
        const root = getRoot(self);

        const userDataSchema = z.object({
          firstName: z.string(),
          lastName: z.string(),
          streetAddress: z.string(),
          zipCode: z.string(),
          city: z.string(),
          email: z.string(),
          phoneNumber: z.string(),
        });

        const userData = userDataSchema.parse({
          firstName: userDataArg.firstName,
          lastName: userDataArg.lastName,
          streetAddress: userDataArg.streetAddress,
          zipCode: userDataArg.zipCode,
          city: userDataArg.city,
          email: userDataArg.email,
          phoneNumber: userDataArg.phoneNumber,
        });

        const response = yield env.baseHttp.post("clients/me", userData);

        const user = root.userStore.processOne(response.data.data);

        self.setActiveUser(user);

        return user;
      }),
    };
  })
  .actions((self) => {
    return {
      logout(redirectHref: Href) {
        self.accessToken = undefined;
        self.activeUser = undefined;
        router.dismissAll();
        router.replace(redirectHref);
      },
    };
  })
  .actions((self) => {
    return {
      setAccessToken: (accessToken?: string) => {
        self.accessToken = accessToken;
      },

      login: flow<boolean, any>(function* login({
        discovery,
        authRequest,
      }: {
        discovery: AuthSession.DiscoveryDocument;
        authRequest: AuthSession.AuthRequest;
      }): any {
        const codeVerifier = authRequest.codeVerifier;

        assert(codeVerifier, "codeVerifier is required");

        const authSessionResult: Await<typeof authRequest.promptAsync> =
          yield authRequest.promptAsync(discovery, {
            windowFeatures: {
              popup: false,
            },
          });

        if (authSessionResult.type === "dismiss") {
          throw new Error("Login was dismissed");
        }

        if (authSessionResult.type !== "success") {
          throw new Error(
            "An error occurred during login. Please try again later.",
          );
        }

        const authorizationCode = authSessionResult.params.code;

        assert(authorizationCode, "authorizationCode is required");

        const tokenResponse: Await<typeof AuthSession.exchangeCodeAsync> =
          yield AuthSession.exchangeCodeAsync(
            {
              clientId: environment.OIDC_CLIENT_ID,
              code: authorizationCode,
              redirectUri: authRequest.redirectUri,
              scopes: environment.OIDC_SCOPES as string[],
              extraParams: {
                code_verifier: codeVerifier,
              },
            },
            discovery,
          );

        const accessToken = tokenResponse.accessToken;
        const refreshToken = tokenResponse.refreshToken;

        assert(refreshToken, "refreshToken is required");

        self.accessToken = accessToken;
        self.refreshToken = refreshToken;

        // // When we set accessToken it triggers the autorun to set the header to the baseHttp axios instance
        // // However, this is not instant, so we need to await a delay of 0 ms just so that all the functions in the stack
        // // are executed before the request including the one that sets the header
        yield delay(0);

        return true;
      }),

      silentLogin: flow(function* silentLogin(): any {
        try {
          const env = getEnv(self);
          const root = getRoot(self);

          // If accessToken and refreshToken is not set, we know the user isn't logged in.
          if (!self.accessToken || !self.refreshToken) return false;

          const tokenDecoded = jwtDecode(self.accessToken);
          const expirationTimeUnix = tokenDecoded.exp;

          if (!expirationTimeUnix) {
            throw new Error("🔑 Error: Token does not have expiration time");
          }

          const timeLeft = dayjs
            .unix(expirationTimeUnix)
            .diff(dayjs(), "second");
          const isExpired = timeLeft < 30;

          if (isExpired) {
            console.log("🔑 Token is expired, refreshing token...");

            const discovery: Await<typeof AuthSession.resolveDiscoveryAsync> =
              yield AuthSession.resolveDiscoveryAsync(
                environment.OIDC_DISCOVERY_URL,
              );

            const tokenResponse: Await<typeof AuthSession.refreshAsync> =
              yield AuthSession.refreshAsync(
                {
                  clientId: environment.OIDC_CLIENT_ID,
                  refreshToken: self.refreshToken,
                  scopes: environment.OIDC_SCOPES as string[],
                },
                discovery,
              );

            const accessToken = tokenResponse.accessToken;
            const refreshToken = tokenResponse.refreshToken;

            assert(refreshToken, "refreshToken is required");

            self.accessToken = accessToken;
            self.refreshToken = refreshToken;
          } else {
            console.log("🔑 Token is not expired, no need to refresh token");
          }

          const response = yield env.baseHttp.get("clients/me");

          const user = root.userStore.processOne(response.data.data);

          self.setActiveUser(user);

          return true;
        } catch (error: any) {
          console.log("error in silent login", error.message);
          // Sentry.captureException(error);
          self.accessToken = undefined;
          self.refreshToken = undefined;
          return false;
        }
      }),

      updateTokens: flow<string, any>(function* updateTokens(): any {
        if (!self.refreshToken || !self.accessToken) return false;

        const tokenDecoded = jwtDecode(self.accessToken);
        const expirationTimeUnix = tokenDecoded.exp;

        if (!expirationTimeUnix) {
          throw new Error("🔑 Error: Token does not have expiration time");
        }

        const timeLeft = dayjs.unix(expirationTimeUnix).diff(dayjs(), "second");
        const isExpired = timeLeft < 30;

        if (isExpired) {
          console.log("🔑 Token is expired, refreshing token...");

          const discovery: Await<typeof AuthSession.resolveDiscoveryAsync> =
            yield AuthSession.resolveDiscoveryAsync(
              environment.OIDC_DISCOVERY_URL,
            );

          const tokenResponse: Await<typeof AuthSession.refreshAsync> =
            yield AuthSession.refreshAsync(
              {
                clientId: environment.OIDC_CLIENT_ID,
                refreshToken: self.refreshToken,
                scopes: environment.OIDC_SCOPES as string[],
              },
              discovery,
            );

          const accessToken = tokenResponse.accessToken;
          const refreshToken = tokenResponse.refreshToken;

          assert(refreshToken, "refreshToken is required");

          self.accessToken = accessToken;
          self.refreshToken = refreshToken;
        } else {
          // console.log("🔑 Token is not expired, no need to refresh token");
        }
      }),

      initializeAccessTokenSync: flow(
        function* initializeAccessTokenSync(): any {
          const env = getEnv(self);

          // sync refreshToken to persistence
          const refreshToken = yield env.persistence.get(
            constants.ASYNC_STORAGE_KEYS.REFRESH_TOKEN,
          );
          self.refreshToken = refreshToken;

          autorun(() => {
            env.persistence.set(
              constants.ASYNC_STORAGE_KEYS.REFRESH_TOKEN,
              self.refreshToken,
            );
          });
          // END - sync refreshToken to persistence

          // sync access token to api and persistence
          const accessTokenFromStorage = yield env.persistence.get(
            constants.ASYNC_STORAGE_KEYS.ACCESS_TOKEN,
          );
          self.accessToken = accessTokenFromStorage;

          autorun(() => {
            env.persistence.set(
              constants.ASYNC_STORAGE_KEYS.ACCESS_TOKEN,
              self.accessToken,
            );
          });

          autorun(() => {
            if (self.accessToken) {
              env.baseHttp.defaults.headers.common["Authorization"] =
                `Bearer ${self.accessToken}`;
            } else {
              delete env.baseHttp.defaults.headers.common["Authorization"];
            }
          });
        },
      ),
    };
  });
